/**
 * SandboxSplash — Full-screen welcome overlay shown on every login
 * to a sandbox environment. Dismisses on click or after 5 seconds.
 */

import React, { useState, useEffect } from 'react';
import { useBranding } from '@/contexts/BrandingContext';
import { AlertTriangle, FlaskConical, X, Clock, Database, Users, FileText } from 'lucide-react';

export default function SandboxSplash() {
  const { isSandbox, sandboxExpiresAt } = useBranding();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isSandbox) return;

    // Show splash on every fresh login (check sessionStorage so it shows once per session)
    const key = 'sandbox_splash_shown';
    if (!sessionStorage.getItem(key)) {
      setVisible(true);
      sessionStorage.setItem(key, '1');

      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => setVisible(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [isSandbox]);

  if (!visible) return null;

  let daysRemaining: number | null = null;
  if (sandboxExpiresAt) {
    const expires = new Date(sandboxExpiresAt);
    daysRemaining = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={() => setVisible(false)}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 text-white">
          <button
            onClick={() => setVisible(false)}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FlaskConical className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Sandbox Environment</h2>
              <p className="text-amber-100 text-sm">Demo instance with sample data</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-gray-600 text-sm leading-relaxed">
            You are working in a <strong>sandbox environment</strong> with pre-loaded demonstration data.
            All data is isolated to this organization and can be freely modified for testing.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <Users className="w-4 h-4 text-indigo-500 flex-shrink-0" />
              <span>50 demo members</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <Database className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>3 programs seeded</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span>7 form templates</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
              {daysRemaining !== null ? (
                <span><strong>{daysRemaining}</strong> days remaining</span>
              ) : (
                <span>No expiration set</span>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Changes made here do not affect production data. This environment may be reset at any time.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
          <button
            onClick={() => setVisible(false)}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
}
